import React, { useState, useCallback } from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';

// ============================================================================
// Types
// ============================================================================

export interface CouponUser {
  id: number;
  email: string;
  createdAt: string;
}

export interface CouponData {
  id: number;
  code: string;
  subscriptionMonths: number;
  currentUses: number;
  maxUses: number | null;
  validFrom: string | null;
  validUntil: string | null;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'inactive' | 'exhausted' | 'expired';
  users: CouponUser[];
}

export interface AdminCouponDetailPageProps {
  coupon: CouponData;
  couponActivateUrl: string;

  // URLs
  couponsPath: string;
  editCouponPath: string;
  userPath: (id: number) => string;

  // Labels
  labels: {
    backLabel: string;
    editLabel: string;
    // Status
    statusTitle: string;
    activeStatus: string;
    inactiveStatus: string;
    exhaustedStatus: string;
    expiredStatus: string;
    // Details
    detailsTitle: string;
    codeLabel: string;
    subscriptionDurationLabel: string;
    currentUsesLabel: string;
    maxUsesLabel: string;
    validFromLabel: string;
    validUntilLabel: string;
    createdAtLabel: string;
    updatedAtLabel: string;
    monthLabel: string;
    monthsLabel: string;
    unlimited: string;
    immediate: string;
    noExpiration: string;
    // Coupon link
    couponLinkTitle: string;
    couponLinkDescription: string;
    copyButton: string;
    copiedButton: string;
    failedButton: string;
    // Users
    usersTitle: string;
    emailColumn: string;
    registeredAtColumn: string;
    actionsColumn: string;
    viewButton: string;
    noUsersYet: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(dateString: string, format: 'short' | 'long' = 'short'): string {
  const date = new Date(dateString);
  if (format === 'long') {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusBadge(
  status: CouponData['status'],
  labels: AdminCouponDetailPageProps['labels']
): { label: string; className: string } {
  switch (status) {
    case 'active':
      return { label: labels.activeStatus, className: 'bg-success' };
    case 'inactive':
      return { label: labels.inactiveStatus, className: 'bg-secondary' };
    case 'exhausted':
      return { label: labels.exhaustedStatus, className: 'bg-warning' };
    case 'expired':
      return { label: labels.expiredStatus, className: 'bg-danger' };
    default:
      return { label: status, className: 'bg-secondary' };
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminCouponDetailPage({
  coupon,
  couponActivateUrl,
  couponsPath,
  editCouponPath,
  userPath,
  labels,
}: AdminCouponDetailPageProps) {
  const [copyButtonText, setCopyButtonText] = useState(labels.copyButton);

  const handleCopyToClipboard = useCallback(async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(couponActivateUrl);
      } else {
        // Fallback for older browsers
        const input = document.createElement('input');
        input.value = couponActivateUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }

      setCopyButtonText(labels.copiedButton);
      setTimeout(() => setCopyButtonText(labels.copyButton), 2000);
    } catch {
      setCopyButtonText(labels.failedButton);
      setTimeout(() => setCopyButtonText(labels.copyButton), 2000);
    }
  }, [couponActivateUrl, labels.copyButton, labels.copiedButton, labels.failedButton]);

  const statusBadge = getStatusBadge(coupon.status, labels);
  const monthLabel =
    coupon.subscriptionMonths === 1 ? labels.monthLabel : labels.monthsLabel;

  return (
    <div>
      <AdminPageHeader
        title={coupon.code}
        subtitle="Coupon details"
        icon="gift"
        backLink={{ href: couponsPath, label: labels.backLabel }}
        actions={
          <a href={editCouponPath} className="btn btn-primary">
            <i className="bi bi-pencil me-1" />
            {labels.editLabel}
          </a>
        }
      />

      {/* Status Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col">
              <h5 className="mb-0">{labels.statusTitle}</h5>
            </div>
            <div className="col-auto">
              <span className={`badge ${statusBadge.className} fs-6`}>
                {statusBadge.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Details Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header">
          <h5 className="mb-0">{labels.detailsTitle}</h5>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.codeLabel}</h6>
              <p className="mb-0">
                <strong>{coupon.code}</strong>
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.subscriptionDurationLabel}</h6>
              <p className="mb-0">
                {coupon.subscriptionMonths} {monthLabel}
              </p>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.currentUsesLabel}</h6>
              <p className="mb-0">{coupon.currentUses}</p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.maxUsesLabel}</h6>
              <p className="mb-0">
                {coupon.maxUses !== null ? coupon.maxUses : labels.unlimited}
              </p>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.validFromLabel}</h6>
              <p className="mb-0">
                {coupon.validFrom ? formatDate(coupon.validFrom, 'long') : labels.immediate}
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.validUntilLabel}</h6>
              <p className="mb-0">
                {coupon.validUntil
                  ? formatDate(coupon.validUntil, 'long')
                  : labels.noExpiration}
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.createdAtLabel}</h6>
              <p className="mb-0">{formatDate(coupon.createdAt, 'long')}</p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-1">{labels.updatedAtLabel}</h6>
              <p className="mb-0">{formatDate(coupon.updatedAt, 'long')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coupon URL Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header">
          <h5 className="mb-0">{labels.couponLinkTitle}</h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-2">{labels.couponLinkDescription}</p>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={couponActivateUrl}
              readOnly
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={handleCopyToClipboard}
            >
              <i
                className={`bi ${
                  copyButtonText === labels.copiedButton
                    ? 'bi-check'
                    : copyButtonText === labels.failedButton
                    ? 'bi-x'
                    : 'bi-clipboard'
                }`}
              />{' '}
              {copyButtonText}
            </button>
          </div>
        </div>
      </div>

      {/* Users Card */}
      <div className="card shadow-sm">
        <div className="card-header">
          <h5 className="mb-0">{labels.usersTitle}</h5>
        </div>
        <div className="card-body">
          {coupon.users.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{labels.emailColumn}</th>
                    <th>{labels.registeredAtColumn}</th>
                    <th>{labels.actionsColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {coupon.users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        <a
                          href={userPath(user.id)}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-eye" /> {labels.viewButton}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0">{labels.noUsersYet}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCouponDetailPage;
